/**
 * 视图定向相关类型定义。
 *
 * 设计说明：
 * 1. 所有有限选项统一使用枚举表达，避免宿主层散落字符串字面量；
 * 2. 该文件只承载协议，不承载具体计算逻辑，保持单一职责；
 * 3. 后续 web-client、CameraManager、视图定向器适配层都复用这里的公共定义。
 */

/**
 * 相机投影类型枚举。
 */
export enum CameraProjectionType {
  /** 透视投影。 */
  Perspective = 'perspective',
  /** 正交投影。 */
  Orthographic = 'orthographic',
}

/**
 * 视图定向命中目标类型枚举。
 */
export enum ViewOrientationTargetKind {
  /** 主面目标。 */
  Face = 'face',
  /** 边面目标。 */
  Edge = 'edge',
  /** 角面目标。 */
  Corner = 'corner',
}

/**
 * 标准视图标识枚举。
 */
export enum StandardViewType {
  /** 前视。 */
  Front = 'front',
  /** 后视。 */
  Back = 'back',
  /** 左视。 */
  Left = 'left',
  /** 右视。 */
  Right = 'right',
  /** 上视。 */
  Top = 'top',
  /** 下视。 */
  Bottom = 'bottom',
  /** 前上视。 */
  FrontTop = 'front-top',
  /** 前下视。 */
  FrontBottom = 'front-bottom',
  /** 前左视。 */
  FrontLeft = 'front-left',
  /** 前右视。 */
  FrontRight = 'front-right',
  /** 后上视。 */
  BackTop = 'back-top',
  /** 后下视。 */
  BackBottom = 'back-bottom',
  /** 后左视。 */
  BackLeft = 'back-left',
  /** 后右视。 */
  BackRight = 'back-right',
  /** 上左视。 */
  TopLeft = 'top-left',
  /** 上右视。 */
  TopRight = 'top-right',
  /** 下左视。 */
  BottomLeft = 'bottom-left',
  /** 下右视。 */
  BottomRight = 'bottom-right',
  /** 前上左等轴测。 */
  FrontTopLeft = 'front-top-left',
  /** 前上右等轴测。 */
  FrontTopRight = 'front-top-right',
  /** 前下左等轴测。 */
  FrontBottomLeft = 'front-bottom-left',
  /** 前下右等轴测。 */
  FrontBottomRight = 'front-bottom-right',
  /** 后上左等轴测。 */
  BackTopLeft = 'back-top-left',
  /** 后上右等轴测。 */
  BackTopRight = 'back-top-right',
  /** 后下左等轴测。 */
  BackBottomLeft = 'back-bottom-left',
  /** 后下右等轴测。 */
  BackBottomRight = 'back-bottom-right',
}

/**
 * 三维向量元组。
 */
export type ViewOrientationVector3Tuple = readonly [number, number, number];

/**
 * 标准视图定义。
 */
export interface StandardViewDefinition {
  /** 标准视图标识。 */
  type: StandardViewType;
  /** 命中目标类型。 */
  kind: ViewOrientationTargetKind;
  /** 宿主展示名称。 */
  label: string;
  /** 相机方向向量，语义为“从目标点指向相机位置”的方向。 */
  direction: ViewOrientationVector3Tuple;
  /** 屏幕朝上向量。 */
  up: ViewOrientationVector3Tuple;
}

/**
 * 标准视图相机帧。
 */
export interface StandardViewCameraFrame {
  /** 相机位置。 */
  position: ViewOrientationVector3Tuple;
  /** 观察目标。 */
  target: ViewOrientationVector3Tuple;
  /** 屏幕朝上方向。 */
  up: ViewOrientationVector3Tuple;
}